import { defineProperty as _defineProperty, slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React from 'react';
import { lsgPre } from '../../config/prefix.js';
import { cleanupClassObject } from '../../utils/DomUtils.js';
import { Host } from '../../utils/Host.js';
import { Key } from '../../utils/Keys.js';
import { fComp } from '../../utils/React.js';
import { SelectOptionPresentation } from '../SelectOption/SelectOptionPresentation.js';
import { FlyoutWrapper } from '../_Flyout/FlyoutWrapper.js';
import { hostClass } from './SelectFlyout.styles.js';

var typeAheadTimeout;
var typeAhead = "";
var getPageScrollNumItems = function getPageScrollNumItems(direction, host, focussedElement) {
  var numItems = 0;
  if (!focussedElement || !host) {
    return 0;
  }
  var hasSibling = !!focussedElement.previousElementSibling || !!focussedElement.nextElementSibling;
  function nextSibling(el) {
    var getElement = function getElement(nextElement) {
      var _a;
      return hasSibling ? nextElement(el) : (_a = nextElement(el.parentElement)) === null || _a === void 0 ? void 0 : _a.children[0];
    };
    return direction === "up" ? getElement(function (ele) {
      return ele.previousElementSibling;
    }) : getElement(function (ele) {
      return ele.nextElementSibling;
    });
  }
  var element = focussedElement;
  var height = element.offsetHeight;
  while (height < host.offsetHeight) {
    var newSibling = nextSibling(element);
    if (!newSibling) {
      break;
    }
    element = newSibling;
    height += element.offsetHeight;
    numItems += 1;
  }
  return numItems;
};
var addTypeAhead = function addTypeAhead(char, options, focussedValue, onFocusValue) {
  window.clearTimeout(typeAheadTimeout);
  typeAhead = "".concat(typeAhead).concat(char);
  var optIndex = options.findIndex(function (o) {
    return !o.disabled && o.label.toLocaleLowerCase().startsWith(typeAhead);
  });
  if (optIndex !== -1) {
    // there is still an element that starts with the current typeahead value, just select this element
    onFocusValue(optIndex);
  } else {
    var _typeAhead$slice$spli = typeAhead.slice(-2).split(""),
      _typeAhead$slice$spli2 = _slicedToArray(_typeAhead$slice$spli, 2),
      before = _typeAhead$slice$spli2[0],
      last = _typeAhead$slice$spli2[1];
    if (before === last) {
      var filteredOptions = options.filter(function (o) {
        return !o.disabled && o.label.toLocaleLowerCase().startsWith(before);
      });
      if (filteredOptions.length > 0) {
        var filteredIndex = focussedValue ? filteredOptions.findIndex(function (o) {
          return o.label === options[focussedValue].label;
        }) : 0;
        onFocusValue(options.findIndex(function (o) {
          return o.label === filteredOptions[filteredIndex >= filteredOptions.length - 1 ? 0 : filteredIndex + 1].label;
        }));
      }
    }
  }
  typeAheadTimeout = window.setTimeout(function () {
    return typeAhead = "";
  }, 1000);
};
var isLetterCharacter = function isLetterCharacter(char) {
  return /^[a-z0-9-_äöüß]$/i.test(char);
};
function handleKeyDown(_ref) {
  var _Key$Home$Key$End$Key;
  var event = _ref.event,
    options = _ref.options,
    focussedIndex = _ref.focussedIndex,
    onChange = _ref.onChange,
    onFocusIndex = _ref.onFocusIndex,
    flyoutElement = _ref.flyoutElement,
    focussedElement = _ref.focussedElement,
    onClose = _ref.onClose,
    useTypeAhead = _ref.useTypeAhead;
  if (!options || options.length === 0) {
    return;
  }
  var selectedIndex = focussedIndex;
  var length = options.length;
  var confirmFn = function confirmFn() {
    var _a;
    // TODO: consider only using -1 instead of undefined within this function.
    if (focussedIndex === undefined || focussedIndex < 0) {
      return undefined;
    }
    if (!((_a = options[focussedIndex]) === null || _a === void 0 ? void 0 : _a.disabled)) {
      onChange(focussedIndex);
    }
    onClose();
  };
  // function that returns a new index for the pressed key
  // the function returns undefined if the option does not change
  var keyFunction = (_Key$Home$Key$End$Key = {}, _defineProperty(_Key$Home$Key$End$Key, Key.Home, function () {
    return 0;
  }), _defineProperty(_Key$Home$Key$End$Key, Key.End, function () {
    return options.length - 1;
  }), _defineProperty(_Key$Home$Key$End$Key, Key.ArrowUp, function () {
    return selectedIndex > 0 ? selectedIndex - 1 : length - 1;
  }), _defineProperty(_Key$Home$Key$End$Key, Key.ArrowDown, function () {
    return selectedIndex + 1 < length ? selectedIndex + 1 : 0;
  }), _defineProperty(_Key$Home$Key$End$Key, Key.PageUp, function () {
    return Math.max((selectedIndex >= 0 ? selectedIndex : 0) - getPageScrollNumItems("up", flyoutElement, focussedElement), 0);
  }), _defineProperty(_Key$Home$Key$End$Key, Key.PageDown, function () {
    return Math.min((selectedIndex >= 0 ? selectedIndex : 0) + getPageScrollNumItems("down", flyoutElement, focussedElement), length - 1);
  }), _defineProperty(_Key$Home$Key$End$Key, Key.Enter, confirmFn), _defineProperty(_Key$Home$Key$End$Key, Key.Escape, function () {
    onClose();
  }), _defineProperty(_Key$Home$Key$End$Key, Key.Tab, function () {
    onClose();
  }), _Key$Home$Key$End$Key)[event.key];
  if (keyFunction) {
    if (event.key !== Key.Tab) {
      // Prevent default (e.g. scroll with arrow keys) for all keys above except Tab
      event.preventDefault();
    }
    var newIndex = keyFunction();
    if (newIndex !== undefined) {
      onFocusIndex(newIndex);
    }
  }
  if (!event.ctrlKey && !event.altKey && useTypeAhead && isLetterCharacter(event.key)) {
    // only do this if the user pressed a character key (a-ü / 0-9)
    addTypeAhead(event.key, options, focussedIndex, onFocusIndex);
  }
}
// Keep in mind that this handled by the Textfield - so there's no magic with the Flyout.
function handleKeydownListFlyout(_ref2) {
  var event = _ref2.event,
    open = _ref2.open,
    focussedIndex = _ref2.focussedIndex,
    options = _ref2.options,
    onChange = _ref2.onChange,
    onOpen = _ref2.onOpen,
    onClose = _ref2.onClose,
    onFocusIndex = _ref2.onFocusIndex,
    flyoutElement = _ref2.flyoutElement,
    focussedElement = _ref2.focussedElement,
    _ref2$afterAction = _ref2.afterAction,
    afterAction = _ref2$afterAction === void 0 ? function () {} : _ref2$afterAction,
    useTypeAhead = _ref2.useTypeAhead;
  if (event.shiftKey) {
    return;
  }
  var handleKeyDownFn = function handleKeyDownFn() {
    return handleKeyDown({
      event: event,
      options: options,
      focussedIndex: focussedIndex,
      onChange: onChange,
      onFocusIndex: onFocusIndex,
      flyoutElement: flyoutElement,
      focussedElement: focussedElement,
      onClose: onClose,
      useTypeAhead: useTypeAhead
    });
  };
  if (open) {
    handleKeyDownFn();
  } else if ([Key.Home, Key.End].includes(event.key) || isLetterCharacter(event.key)) {
    onOpen();
    handleKeyDownFn();
  } else if ([Key.ArrowUp, Key.ArrowDown, Key.Enter, Key.Space].includes(event.key)) {
    onOpen();
  }
  afterAction();
}
var SelectFlyoutPresentation = fComp(function (_ref3) {
  var _cleanupClassObject;
  var id = _ref3.id,
    className = _ref3.className,
    noMargin = _ref3.noMargin,
    name = _ref3.name,
    emptyListLabel = _ref3.emptyListLabel,
    options = _ref3.options,
    onChange = _ref3.onChange,
    _onMouseOver = _ref3.onMouseOver,
    focussedValue = _ref3.focussedValue,
    value = _ref3.value,
    hostRef = _ref3.hostRef,
    focussedRef = _ref3.focussedRef,
    onKeyDown = _ref3.onKeyDown,
    isToggleElmWidth = _ref3.isToggleElmWidth,
    hasTabIndex = _ref3.hasTabIndex,
    toggleElement = _ref3.toggleElement,
    toggleContainerElement = _ref3.toggleContainerElement,
    open = _ref3.open,
    onCloseClick = _ref3.onCloseClick,
    role = _ref3.role,
    ariaLabel = _ref3.ariaLabel;
  return /*#__PURE__*/React.createElement(Host, {
    className: cleanupClassObject((_cleanupClassObject = {}, _defineProperty(_cleanupClassObject, "".concat(hostClass), true), _defineProperty(_cleanupClassObject, className, className), _defineProperty(_cleanupClassObject, "".concat(lsgPre, "no-margin"), noMargin), _cleanupClassObject))
  }, /*#__PURE__*/React.createElement(FlyoutWrapper, {
    id: id,
    className: "".concat(hostClass, "-flyout"),
    open: open,
    onClose: onCloseClick,
    toggleElement: toggleElement,
    toggleContainerElement: toggleContainerElement,
    onKeyDown: onKeyDown,
    isToggleElmWidth: isToggleElmWidth,
    hasTabIndex: hasTabIndex,
    hostRef: hostRef,
    withGap: true,
    role: role,
    ariaLabel: ariaLabel
  }, emptyListLabel && (options === null || options === void 0 ? void 0 : options.length) === 0 && /*#__PURE__*/React.createElement(SelectOptionPresentation, {
    label: emptyListLabel
  }), options.map(function (option, i) {
    return /*#__PURE__*/React.createElement(SelectOptionPresentation, Object.assign({}, option, {
      id: "".concat(id, "-option_").concat(option.value),
      key: i,
      highlight: option.value === focussedValue,
      selected: option.value === value,
      onClick: function onClick(event) {
        return onChange(option.value, name, event);
      },
      onMouseOver: function onMouseOver() {
        return _onMouseOver(option.value);
      },
      ref: option.value === focussedValue ? focussedRef : undefined
    }));
  })));
});
SelectFlyoutPresentation.displayName = "SelectFlyoutPresentation";

export { SelectFlyoutPresentation, handleKeyDown, handleKeydownListFlyout };
